import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtClock } from '../lib/format.js';

// Shows published, unassigned shifts at this user's authorized locations
// in the next 28 days. Each card has a "Pick up" button that claims the
// shift for the current user. Only published opens show — drafts stay
// invisible to staff. Hidden when there are no opens to avoid clutter.
export function OpenShiftsForPickup({ user, onPickedUp }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true); setError('');
    try {
      const r = await api('GET', '/api/shifts/open-for-me', null, { 'x-user-id': String(user.id) });
      setShifts(r.shifts || []);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function pickUp(s) {
    if (!confirm(`Pick up the shift on ${s.shift_date} from ${s.start_time} to ${s.end_time} at ${s.location_name}?`)) return;
    setBusyId(s.id); setError('');
    try {
      await api('POST', `/api/shifts/${s.id}/pickup`, {}, { 'x-user-id': String(user.id) });
      await load();
      if (onPickedUp) onPickedUp();
    } catch (e) {
      let msg = e.message || 'Pickup failed';
      try { const p = JSON.parse(msg); msg = p.error || msg; } catch {}
      setError(msg);
    }
    finally { setBusyId(null); }
  }

  if (loading) return null;
  if (shifts.length === 0 && !error) return null;

  return (
    <div className="card" style={{ borderLeft: '4px solid var(--gold, #c8a25b)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#37474f', marginBottom: 4 }}>
        Available shifts to pick up ({shifts.length})
      </div>
      <div style={{ fontSize: 11, color: '#757575', marginBottom: 8 }}>
        Open shifts your manager hasn't assigned yet. First-come, first-served.
      </div>
      {error && (
        <div style={{ fontSize: 12, color: '#b91c1c', background: '#fee', padding: 6, borderRadius: 4, marginBottom: 8 }}>
          {error}
        </div>
      )}
      <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {shifts.map((s) => {
          const date = new Date(s.shift_date + 'T12:00:00Z');
          const dayLabel = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()];
          const friendly = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return (
            <div key={s.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 10, background: '#fffdf3' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#37474f' }}>{dayLabel} · {friendly}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#212121', marginTop: 2 }}>
                {fmtClock(s.start_time)}–{fmtClock(s.end_time)}
              </div>
              <div style={{ fontSize: 11, color: '#757575' }}>
                {s.location_name}{s.role ? ` · ${s.role}` : ''}
              </div>
              {s.notes && <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>{s.notes}</div>}
              <button onClick={() => pickUp(s)} disabled={busyId === s.id}
                style={{ marginTop: 8, width: '100%', padding: '6px 10px', fontSize: 12, fontWeight: 600,
                          background: busyId === s.id ? '#ccc' : 'var(--forest, #1B2B1B)', color: '#fff',
                          border: 'none', borderRadius: 6, cursor: busyId === s.id ? 'wait' : 'pointer',
                          fontFamily: 'inherit' }}>
                {busyId === s.id ? 'Picking up…' : 'Pick up'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
